import React, { useState } from 'react';
import { Heart, Send } from 'lucide-react';

interface WishesInteractiveSectionProps {
  invitationId: string | null;
  isLive: boolean;
  language: 'en' | 'mr';
  onWishAdded: () => void;
  totalWishes: number;
}

interface Wish {
  id: string;
  name: string;
  message: string;
  reactions: number;
  createdAt: Date;
}

export const WishesInteractiveSection: React.FC<WishesInteractiveSectionProps> = ({
  invitationId,
  isLive,
  language,
  onWishAdded,
  totalWishes,
}) => {
  const [wishes, setWishes] = useState<Wish[]>([
    {
      id: '1',
      name: 'Friend 1',
      message: language === 'mr' ? 'जन्मदिन मुबारक! 🎉' : 'Happy Birthday! 🎉',
      reactions: 5,
      createdAt: new Date(),
    },
  ]);
  const [newName, setNewName] = useState('');
  const [newMessage, setNewMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmitWish = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newName.trim() || !newMessage.trim()) return;

    setIsSubmitting(true);

    try {
      const wish: Wish = {
        id: Date.now().toString(),
        name: newName,
        message: newMessage,
        reactions: 0,
        createdAt: new Date(),
      };

      setWishes((prev) => [wish, ...prev]);
      setNewName('');
      setNewMessage('');
      onWishAdded();
    } catch (error) {
      console.error('Failed to submit wish:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const addReaction = (id: string) => {
    setWishes((prev) =>
      prev.map((w) => (w.id === id ? { ...w, reactions: w.reactions + 1 } : w))
    );
  };

  return (
    <section className="relative py-24 px-4">
      <div className="max-w-4xl mx-auto">
        <h2 className="text-center text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-pink-400 to-purple-400 mb-4">
          {language === 'mr' ? '❤️ आशीर्वाद भाग करा' : '❤️ Share Birthday Wishes'}
        </h2>
        <p className="text-center text-gray-400 mb-12">
          {language === 'mr'
            ? `अब तक ${totalWishes} शुभेच्छा`
            : `${totalWishes} wishes so far`}
        </p>

        {/* Wish Form */}
        <form onSubmit={handleSubmitWish} className="mb-12 bg-gradient-to-br from-slate-900/50 to-slate-800/50 border border-cyan-400/30 rounded-2xl p-8">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-300 mb-2">
                {language === 'mr' ? 'आपले नाव' : 'Your Name'}
              </label>
              <input
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder={language === 'mr' ? 'नाव' : 'Enter your name'}
                className="w-full bg-slate-800 border border-slate-700 text-white rounded-lg px-4 py-3 focus:border-cyan-400 focus:ring-2 focus:ring-cyan-400/20 transition-all"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-300 mb-2">
                {language === 'mr' ? 'आपली शुभेच्छा' : 'Your Wish'}
              </label>
              <textarea
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder={language === 'mr' ? 'जन्मदिन मुबारक!' : 'Write your birthday wish...'}
                rows={4}
                className="w-full bg-slate-800 border border-slate-700 text-white rounded-lg px-4 py-3 focus:border-pink-400 focus:ring-2 focus:ring-pink-400/20 transition-all resize-none"
              />
            </div>

            <button
              type="submit"
              disabled={isSubmitting || !newName.trim() || !newMessage.trim()}
              className="w-full bg-gradient-to-r from-cyan-500 to-pink-500 text-white font-bold py-3 rounded-lg hover:shadow-lg hover:shadow-cyan-500/50 transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <Send size={18} />
              {language === 'mr' ? 'शुभेच्छा पाठवा' : 'Send Wish'}
            </button>
          </div>
        </form>

        {/* Wishes Display */}
        <div className="space-y-4 max-h-96 overflow-y-auto">
          {wishes.length === 0 ? (
            <div className="text-center text-gray-500 py-12">
              {language === 'mr' ? 'कोणतीही शुभेच्छा अभी तक नाहीं' : 'No wishes yet. Be the first to share!'}
            </div>
          ) : (
            wishes.map((wish) => (
              <div
                key={wish.id}
                className="group bg-gradient-to-r from-slate-900/50 to-slate-800/50 border border-slate-700 hover:border-pink-400/50 rounded-xl p-6 transition-all hover:shadow-lg hover:shadow-pink-500/10"
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="font-bold text-white">{wish.name}</p>
                    <p className="text-xs text-gray-500">
                      {wish.createdAt.toLocaleTimeString()}
                    </p>
                  </div>
                  <button
                    onClick={() => addReaction(wish.id)}
                    className="group/btn p-2 hover:bg-pink-500/20 rounded-lg transition-all"
                  >
                    <Heart
                      size={18}
                      className="text-pink-400 group-hover/btn:fill-pink-400 transition-all"
                    />
                  </button>
                </div>

                <p className="text-gray-300 mb-4">{wish.message}</p>

                <div className="flex items-center gap-2 text-sm">
                  <Heart size={14} className="text-pink-400 fill-pink-400" />
                  <span className="text-gray-400">{wish.reactions} reactions</span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </section>
  );
};
